<script lang="ts">
	/**
	 * SortableTableHeadCell.svelte — table header cell that toggles server-side sorting.
	 *
	 * Renders a `TableHeadCell` whose label is a button. Clicking it asks the
	 * parent to sort by {@link key}; clicking the already-active column flips
	 * the direction. The parent owns the `sortBy` / `sortDir` state and is
	 * responsible for resetting pagination and re-fetching from the server —
	 * this component never sorts rows locally.
	 */

	import { TableHeadCell } from 'flowbite-svelte';
	import type { Snippet } from 'svelte';
	import type { SortDirection } from '../AppTypes';

	interface Props {
		/** Sort key sent to the server as `sortBy`. Must be whitelisted server-side. */
		key: string;
		/** The currently active sort key across the whole table. */
		sortBy: string;
		/** The currently active sort direction. */
		sortDir: SortDirection;
		/** Direction applied the first time this column is activated. */
		defaultDir?: SortDirection;
		/** Extra classes forwarded to the underlying `TableHeadCell`. */
		class?: string;
		/** Invoked with the new sort key and direction when the header is clicked. */
		onsort: (_key: string, _dir: SortDirection) => void;
		/** Header label content. */
		children: Snippet;
	}

	let { key, sortBy, sortDir, defaultDir = 'asc', class: className = '', onsort, children }: Props = $props();

	/** Whether this column is the one currently driving the ordering. */
	let active = $derived(sortBy === key);

	/**
	 * Icon reflecting the sort state: a neutral glyph when inactive, and an
	 * up/down caret matching the direction when this column is active.
	 */
	let icon = $derived(active ? (sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort');

	/** Toggle direction on the active column, otherwise activate this one. */
	function handleClick(): void {
		onsort(key, active ? (sortDir === 'asc' ? 'desc' : 'asc') : defaultDir);
	}
</script>

<TableHeadCell class={className} aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
	<button
		type="button"
		class="group inline-flex cursor-pointer items-center gap-1.5 uppercase hover:text-gray-900 dark:hover:text-white"
		onclick={handleClick}
	>
		{@render children()}
		<i
			class="fa-solid {icon} h-3 w-3 text-xs {active
				? 'text-primary-600 dark:text-primary-400'
				: 'text-gray-400 opacity-0 transition-opacity group-hover:opacity-100'}"
		></i>
	</button>
</TableHeadCell>
